/**
 * MyListingsPage - Partner Listings Management Dashboard
 *
 * Features:
 * - Tab interface (Published & History / Drafts)
 * - Status-aware action buttons (Publish/Unpublish/Republish)
 * - Optimistic UI updates
 * - New unified ListingsApi integration
 */
import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Home,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  RefreshCw,
} from "lucide-react";

// Hooks & Contexts
import { useDeduplicatedEffect } from "@/shared/hooks";
import { observer } from "mobx-react-lite";
import { useStore } from "@/stores";

// Utils

// New Design System
import { BasePage } from "@/shared/ui/layout/BasePage";
import { Box, Text } from "@/shared/ui/base";
import { Card, Button, ConfirmModal, Tooltip } from "@/shared/ui/components";

// Components
import { LoadingScreen } from "@/shared/ui/components";

// API
import { PublicationStatus } from "@/features/listings/api/listingsApi";
import type { Listing } from "@/shared/types/listings";

// Constants
import { ROUTES } from "@/shared/constants";

// Initialize API removed - using Store instead

// Helper: Check if user is partner
function isPartner(
  authMe: { orgs?: Array<{ role: string }> } | null | undefined,
): boolean {
  return authMe?.orgs?.some((org) => org.role.startsWith("partner_")) ?? false;
}

// Helper: Extract featured image or first image from images_by_section
function getFirstImage(listing: Listing): string | null {
  const sections = listing.media.images_by_section || {};

  // First, try to find the featured image across all sections
  for (const images of Object.values(sections)) {
    const featuredImage = images.find((img) => img.is_featured);
    if (featuredImage) {
      return featuredImage.thumbnail_url || featuredImage.url;
    }
  }

  // Fall back to first available image
  const allImages = Object.values(sections).flat();
  return allImages[0]?.thumbnail_url || allImages[0]?.url || null;
}

type TabType = "published" | "drafts";

// Extended listing type with translated location
interface TranslatedListing extends Listing {
  translatedPrefecture: string;
  translatedCity: string;
  translatedWard: string;
}

export const MyListingsPage = observer(function MyListingsPage() {
  const navigate = useNavigate();
  const { listingsStore, authStore, uiStore } = useStore();
  const { showSnackbar, t, locale } = uiStore;
  const {
    listings: storeListings,
    loading: storeLoading,
    error: storeError,
  } = listingsStore;
  const { authMe } = authStore;

  const userIsPartner = useMemo(() => isPartner(authMe), [authMe]);

  const currentLang = locale;

  // State
  // listings state removed - using storeListings
  const [translatedListings, setTranslatedListings] = useState<
    TranslatedListing[]
  >([]);
  const [activeTab, setActiveTab] = useState<TabType>("published");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);

  // Delete confirmation modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [listingToDelete, setListingToDelete] = useState<string | null>(null);

  // Filtered listings by tab - use translated listings
  const publishedListings = useMemo(
    () =>
      translatedListings.filter(
        (l) =>
          l.publication_status === PublicationStatus.PUBLISHED ||
          l.publication_status === PublicationStatus.EXPIRED,
      ),
    [translatedListings],
  );

  const draftListings = useMemo(
    () =>
      translatedListings.filter(
        (l) => l.publication_status === PublicationStatus.DRAFT,
      ),
    [translatedListings],
  );

  const activeListings =
    activeTab === "published" ? publishedListings : draftListings;

  // Load listings
  const loadListings = useCallback(async () => {
    // Determine if we need to show loading spinner (only on empty/initial)
    // Store handles loading state internally
    try {
      await listingsStore.fetchMyListings();
    } catch {
      // Store handles error state
    }
  }, [listingsStore]);

  useDeduplicatedEffect(() => {
    if (userIsPartner) {
      loadListings();
    }
  }, [userIsPartner, loadListings]);

  // Translate listings when they change or language changes
  useDeduplicatedEffect(() => {
    if (storeListings.length === 0) {
      setTranslatedListings([]);
      return;
    }

    const translated = storeListings.map((listing: Listing) => {
      return {
        ...listing,
        translatedPrefecture:
          (listing.location.prefecture as any)[currentLang] ||
          listing.location.prefecture.en,
        translatedCity:
          (listing.location.city as any)[currentLang] ||
          listing.location.city.en,
        translatedWard:
          (listing.location.ward as any)?.[currentLang] ||
          listing.location.ward?.en ||
          "",
      };
    });

    setTranslatedListings(translated);
  }, [storeListings, currentLang]);

  // Handle status update (Publish/Unpublish/Republish)
  const handleStatusUpdate = useCallback(
    async (listingId: string, newStatus: PublicationStatus) => {
      setUpdatingStatusId(listingId);

      try {
        await listingsStore.updateListing(listingId, {
          publication_status: newStatus,
        });

        const actionText =
          newStatus === PublicationStatus.PUBLISHED
            ? t("myListingsPublished" as any)
            : newStatus === PublicationStatus.EXPIRED
              ? t("myListingsUnpublished" as any)
              : t("myListingsStatusUpdated" as any);

        showSnackbar(actionText, "success");
      } catch {
        showSnackbar(t("myListingsStatusUpdateError"), "error");
      } finally {
        setUpdatingStatusId(null);
      }
    },
    [listingsStore, showSnackbar, t],
  );

  // Handle delete
  const handleDeleteConfirm = useCallback(async () => {
    if (!listingToDelete) return;

    setDeletingId(listingToDelete);
    setDeleteModalOpen(false);

    try {
      await listingsStore.deleteListing(listingToDelete);
      showSnackbar(t("myListingsDeleted"), "success");
    } catch {
      showSnackbar(t("myListingsDeleteError"), "error");
    } finally {
      setDeletingId(null);
      setListingToDelete(null);
    }
  }, [listingToDelete, listingsStore, showSnackbar, t]);

  const openDeleteModal = (listingId: string) => {
    setListingToDelete(listingId);
    setDeleteModalOpen(true);
  };

  // Redirect non-partners
  if (!userIsPartner) {
    return (
      <BasePage
        title={t("myListingsTitle")}
        description="My property listings"
        maxWidth="lg"
        background="gray"
      >
        <Card variant="default" padding="lg" className="text-center">
          <Text variant="body" color="secondary">
            {t("createListingPartnerOnly")}
          </Text>
          <Button
            variant="primary"
            onClick={() => navigate(ROUTES.PROFILE)}
            className="mt-4"
          >
            {t("backToProfile")}
          </Button>
        </Card>
      </BasePage>
    );
  }

  // Show loading state
  // We use storeLoading but also check if we have listings to determine "blocking" load
  if (storeLoading && storeListings.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <>
      <BasePage
        title={t("myListingsTitle")}
        description="Manage your property listings"
        maxWidth="xl"
        background="gray"
      >
        {/* Header */}
        <Box className="flex items-center justify-between mb-6">
          <Box className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(ROUTES.PROFILE)}
              className="-ml-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Box>
              <Text variant="h2" className="text-gray-900">
                {t("myListingsTitle")}
              </Text>
              <Text variant="body" color="secondary">
                {t("myListingsSubtitle")}
              </Text>
            </Box>
          </Box>
          <Button
            variant="primary"
            onClick={() => navigate(ROUTES.CREATE_LISTING)}
          >
            <Plus className="w-5 h-5 mr-2" />
            {t("profileCreateListing")}
          </Button>
        </Box>

        {/* Error state */}
        {storeError && (
          <Card
            variant="default"
            padding="md"
            className="bg-red-50 border-red-200 mb-6"
          >
            <Box className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <Text variant="body" className="text-red-700 flex-1">
                {storeError} // Use store error state
              </Text>
              <Button
                variant="ghost"
                size="sm"
                onClick={loadListings}
                className="text-red-700 underline"
              >
                {t("overviewRetry")}
              </Button>
            </Box>
          </Card>
        )}

        {/* Tab Navigation */}
        <Box className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab("published")}
            className={`px-4 py-3 font-medium text-sm transition-colors border-b-2 ${
              activeTab === "published"
                ? "text-red-600 border-red-600"
                : "text-gray-600 border-transparent hover:text-gray-900"
            }`}
          >
            {t("myListingsPublishedTab")} ({publishedListings.length})
          </button>
          <button
            onClick={() => setActiveTab("drafts")}
            className={`px-4 py-3 font-medium text-sm transition-colors border-b-2 ${
              activeTab === "drafts"
                ? "text-red-600 border-red-600"
                : "text-gray-600 border-transparent hover:text-gray-900"
            }`}
          >
            {t("myListingsDraftsTab")} ({draftListings.length})
          </button>
        </Box>

        {/* Empty state */}
        {!storeError && activeListings.length === 0 && (
          <Card variant="default" padding="lg" className="text-center">
            <Home className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <Text variant="h4" className="mb-2">
              {activeTab === "published"
                ? t("myListingsEmptyPublished")
                : t("myListingsEmptyDrafts")}
            </Text>
            <Text variant="body" color="secondary" className="mb-6">
              {activeTab === "published"
                ? t("myListingsEmptyPublishedDesc")
                : t("myListingsEmptyDraftsDesc")}
            </Text>
            <Button
              variant="primary"
              onClick={() => navigate(ROUTES.CREATE_LISTING)}
            >
              <Plus className="w-5 h-5 mr-2" />
              {t("myListingsCreateFirst")}
            </Button>
          </Card>
        )}

        {/* Listings grid */}
        {activeListings.length > 0 && (
          <Box className="grid gap-4">
            {activeListings.map((listing) => {
              const firstImage = getFirstImage(listing);
              const isDeleting = deletingId === listing.id;
              const isUpdating = updatingStatusId === listing.id;
              const isProcessing = isDeleting || isUpdating;

              return (
                <Card
                  key={listing.id}
                  variant="default"
                  padding="md"
                  className={`flex items-center gap-4 ${isProcessing ? "opacity-50" : ""}`}
                >
                  {/* Thumbnail */}
                  <Box className="w-24 h-24 bg-gray-100 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden">
                    {firstImage ? (
                      <img
                        src={firstImage}
                        alt={listing.title.ja}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Home className="w-8 h-8 text-gray-400" />
                    )}
                  </Box>

                  {/* Info */}
                  <Box className="flex-1 min-w-0">
                    <Box className="flex items-center gap-2 mb-1">
                      <Text variant="h5" className="text-gray-900 truncate">
                        {listing.title.ja}
                      </Text>
                      {/* Status Badge */}
                      {listing.publication_status ===
                        PublicationStatus.PUBLISHED && (
                        <Text
                          as="span"
                          variant="caption"
                          className="flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded"
                        >
                          <Eye className="w-3 h-3" />
                          {t("myListingsPublished")}
                        </Text>
                      )}
                      {listing.publication_status ===
                        PublicationStatus.EXPIRED && (
                        <Text
                          as="span"
                          variant="caption"
                          className="flex items-center gap-1 bg-gray-100 text-gray-600 px-2 py-0.5 rounded"
                        >
                          <EyeOff className="w-3 h-3" />
                          {t("myListingsExpired")}
                        </Text>
                      )}
                      {listing.publication_status ===
                        PublicationStatus.DRAFT && (
                        <Text
                          as="span"
                          variant="caption"
                          className="flex items-center gap-1 bg-gray-100 text-gray-600 px-2 py-0.5 rounded"
                        >
                          <EyeOff className="w-3 h-3" />
                          {t("myListingsDraft")}
                        </Text>
                      )}
                    </Box>
                    <Text
                      variant="body"
                      className="text-sm text-gray-600 truncate"
                    >
                      {listing.translatedPrefecture}{" "}
                      {listing.translatedCity && `- ${listing.translatedCity}`}
                      {listing.translatedWard && `, ${listing.translatedWard}`}
                    </Text>
                    <Text
                      variant="body"
                      className="text-sm font-medium text-red-600 mt-1"
                    >
                      ¥{listing.financials.rent.amount.toLocaleString()}/
                      {t("listings.month")}
                    </Text>
                  </Box>

                  {/* Actions - Icon-Only with Tooltips (Status → Edit → Delete) */}
                  <Box className="flex items-center gap-2">
                    {/* 1. Status Action (Leftmost) - Context-aware */}
                    {listing.publication_status === PublicationStatus.DRAFT && (
                      <Tooltip content={t("myListingsPublishAction")}>
                        <Button
                          variant="primary"
                          size="icon"
                          onClick={() =>
                            handleStatusUpdate(
                              listing.id,
                              PublicationStatus.PUBLISHED,
                            )
                          }
                          disabled={isProcessing}
                        >
                          <CheckCircle className="w-5 h-5" />
                        </Button>
                      </Tooltip>
                    )}
                    {listing.publication_status ===
                      PublicationStatus.PUBLISHED && (
                      <Tooltip content={t("myListingsUnpublish")}>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            handleStatusUpdate(
                              listing.id,
                              PublicationStatus.EXPIRED,
                            )
                          }
                          disabled={isProcessing}
                        >
                          <EyeOff className="w-5 h-5" />
                        </Button>
                      </Tooltip>
                    )}
                    {listing.publication_status ===
                      PublicationStatus.EXPIRED && (
                      <Tooltip content={t("myListingsRepublish")}>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            handleStatusUpdate(
                              listing.id,
                              PublicationStatus.PUBLISHED,
                            )
                          }
                          disabled={isProcessing}
                        >
                          <RefreshCw className="w-5 h-5" />
                        </Button>
                      </Tooltip>
                    )}

                    {/* 2. Edit Action (Middle) */}
                    <Tooltip content={t("myListingsEdit")}>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/listings/${listing.id}/edit`)}
                        disabled={isProcessing}
                      >
                        <Edit className="w-5 h-5" />
                      </Button>
                    </Tooltip>

                    {/* 3. Delete Action (Rightmost) */}
                    <Tooltip content={t("myListingsDelete")}>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDeleteModal(listing.id)}
                        disabled={isProcessing}
                        className="hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </Tooltip>
                  </Box>
                </Card>
              );
            })}
          </Box>
        )}
      </BasePage>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setListingToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t("myListingsDeleteTitle")}
        message={t("myListingsDeleteMessage")}
        confirmText={t("myListingsDeleteConfirm")}
        cancelText={t("cancel")}
        variant="danger"
        loading={deletingId !== null}
      />
    </>
  );
});
